import React, { useState } from "react";
import type { ReactNode } from "react";
import { Platform, StyleSheet, View, Pressable } from "react-native";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import { MagnifyingGlass } from "../../../components/svgs/common";
import { DataTable } from "react-native-paper";
import { NewCustomActionButton } from "../../../components/common/general/CustomButton/NewCustomActionButton";
import CustomAdminModal from "../../common/general/CustomAdminModal/CustomAdminModal";
import CustomSpacing from "../layout/CustomSpacing/CustomSpacing";
import CustomButton from "../general/CustomButton/CustomButton";
import { colors } from "../../../styles/design-system";

type SearchListModalProps = {
  visible: boolean;
  onClose: () => void;
  onAction: (id: string | string[]) => void;
  list: { id: string; name: string }[];
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  title?: string;
  multiSelect?: boolean;
  actionLabel?: string;
  actionIcon?: ReactNode;
  multiSelectConfirmLabel?: string;
};

const SearchListModal = ({
  visible,
  onClose,
  onAction,
  list,
  searchQuery,
  setSearchQuery,
  title,
  multiSelect = false,
  actionLabel,
  actionIcon,
  multiSelectConfirmLabel,
}: SearchListModalProps) => {
  const { isMedium } = useResponsiveScreen();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const resolvedActionLabel = actionLabel ?? "Select";
  const resolvedMultiSelectLabel = multiSelectConfirmLabel ?? "Confirm Selection";

  const handleItemToggle = (id: string) => {
    if (multiSelect) {
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
    } else {
      // Single selection - execute action immediately
      onAction(id);
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (multiSelect && selectedIds.size > 0) {
      await onAction(Array.from(selectedIds));
      setSelectedIds(new Set());
      onClose();
    }
  };

  const handleModalClose = () => {
    setSelectedIds(new Set());
    onClose();
  };

  return (
    <CustomAdminModal
      visible={visible}
      onRequestClose={handleModalClose}
      title={title}
      exitFill="#6B7280"
      onShow={() => {}}
      ariaLabel="Search For Assignment Action Modal"
      style={{ width: isMedium ? "50%" : "90%" }}>
      <View style={styles.container}>
        <CustomSpacing type="vertical" size="s" />
        <View style={styles.searchContainer}>
          <MagnifyingGlass style={styles.searchIcon} />
          <CustomTextInput
            placeholder="Search by any value"
            value={searchQuery}
            onChangeText={setSearchQuery}
            style={[
              styles.searchInput,
              Platform.select({
                web: {
                  outlineStyle: "none",
                  outlineWidth: 0,
                  WebkitAppearance: "none",
                } as unknown,
              }),
            ]}
            placeholderTextColor={colors.grey[500]}
            selectionColor="#6B7280"
            cursorColor="#6B7280"
          />
        </View>

        {multiSelect && selectedIds.size > 0 && (
          <View style={styles.selectionBanner}>
            <CustomText
              text={`${selectedIds.size} selected`}
              size="s"
              weight="medium"
              useTranslationText={false}
              style={styles.selectionText}
            />
          </View>
        )}

        <DataTable>
          {list.map((item) => {
            const isSelected = selectedIds.has(item.id);

            return (
              <DataTable.Row
                key={item.id}
                style={isSelected ? styles.selectedRow : undefined}
                onPress={multiSelect ? () => handleItemToggle(item.id) : undefined}>
                <DataTable.Cell
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    justifyContent: "space-between",
                  }}>
                  <View
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "row",
                      gap: 8,
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingVertical: 12,
                    }}>
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 8,
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}>
                      {multiSelect && (
                        <Pressable
                          style={[styles.checkbox, isSelected && styles.checkboxSelected]}
                          onPress={() => handleItemToggle(item.id)}>
                          {isSelected && <View style={styles.checkboxInner} />}
                        </Pressable>
                      )}
                      <CustomText text={item.name} size="s" weight="medium" useTranslationText={false} style={{}} />
                    </View>
                    {!multiSelect && (
                      <NewCustomActionButton
                        text={resolvedActionLabel}
                        onPress={async () => {
                          await onAction(item.id);
                          onClose();
                        }}
                        leftIcon={actionIcon}
                      />
                    )}
                  </View>
                </DataTable.Cell>
              </DataTable.Row>
            );
          })}
        </DataTable>

        {multiSelect && (
          <View style={styles.footerButtons}>
            <CustomButton text="Cancel" styleType="secondary" onPress={handleModalClose} style={styles.cancelButton} />
            <CustomButton
              text={resolvedMultiSelectLabel}
              styleType="primary"
              onPress={handleConfirm}
              style={styles.confirmButton}
              disabled={selectedIds.size === 0}
            />
          </View>
        )}
      </View>
    </CustomAdminModal>
  );
};

export default SearchListModal;

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 40,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  searchIcon: {
    width: 20,
    height: 20,
  },
  searchInput: {
    flex: 1,
  },
  selectionBanner: {
    backgroundColor: "#EEF2FF",
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    marginBottom: 8,
  },
  selectionText: {
    color: "#3E68FE",
  },
  selectedRow: {
    backgroundColor: "#EEF2FF",
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
  checkboxSelected: {
    backgroundColor: "#3E68FE",
    borderColor: "#3E68FE",
  },
  checkboxInner: {
    width: 10,
    height: 10,
    borderRadius: 2,
    backgroundColor: "#FFFFFF",
  },
  footerButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  cancelButton: {
    minWidth: 100,
  },
  confirmButton: {
    minWidth: 150,
  },
});
